import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Enquiries } from './enquiries.actions';
import { catchError, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { EnquiriesService, OrganisationDto } from '@careapp-group/ng-sdk';
import { Injectable } from '@angular/core';
import { getSelected, getSelectedId, org, ORGANISATIONS, statistics } from '../../store/reducers';
import { Store } from '@ngrx/store';
import { getEnquiriesOrgInsightsFilter } from './enquiries.selectors';
import { StreamAction } from '../../store/stream/stream.actions';
import { ChatClientService } from 'stream-chat-angular';
import { GetUnreadCountAPIResponse } from 'stream-chat';
import { Router } from '@angular/router';
import * as selectors from '../../store/reducers/index';

@Injectable()
export class EnquiriesEffects {
  // Sets unread count for enquiries on initialisation, listens for further unread events.
  listenForUnreadCount = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StreamAction.responseSuccessInitStream),
        tap(async (_) => {
          this.getUnreadCountAndDispatch();

          this.chatClientService.chatClient.on((event) => {
            switch (event.type) {
              case 'notification.message_new':
                if (event.channel_type == 'commerce') {
                  this.getUnreadCountAndDispatch();
                }
              case 'channel.created':
                if (event.channel_type == 'commerce') {
                  this.getUnreadCountAndDispatch();
                }
              case 'message.new':
                if (event.channel_type == 'commerce') {
                  this.getUnreadCountAndDispatch();
                }
                break;
              case 'message.read':
                if (event.channel_type == 'commerce') {
                  this.getUnreadCountAndDispatch();
                }
                break;
            }
          });
        }),
      ),
    {
      dispatch: false,
    },
  );

  // Retrieves the unread count for commerce channels only.
  // Updates store unread count if a new non-zero count is found.
  async getUnreadCountAndDispatch(): Promise<void> {
    const unreadMessageCount: GetUnreadCountAPIResponse =
      await this.chatClientService?.chatClient?.getUnreadCount();

    const unreadCount =
      unreadMessageCount.channel_type?.find((channel) => channel.channel_type === 'commerce')
        ?.channel_count ?? 0;

    this.store.dispatch(Enquiries.updateUnreadEnquiryCount({ count: unreadCount }));
  }

  updateUnreadOnClear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StreamAction.successMarkAllRead),
      map(() => Enquiries.updateUnreadEnquiryCount({ count: 0 })),
    ),
  );

  completeEnquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Enquiries.requestCompleteEnquiry),
      switchMap((action) => {
        return this.api.completeEnquiry({ id: action.enquiryId }).pipe(
          map((res) => {
            return Enquiries.successCompleteEnquiry({ enquiryId: action.enquiryId });
          }),
          catchError((error) =>
            of(Enquiries.failCompleteEnquiry({ enquiryId: action.enquiryId, error })),
          ),
        );
      }),
    ),
  );

  listEnquiries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Enquiries.RequestAllForSegmentAction),
      withLatestFrom(
        this.store.select(getSelected(ORGANISATIONS)),
        this.store.select(getEnquiriesOrgInsightsFilter),
        this.store.select(statistics.getFilters),
      ),
      switchMap(([action, org, enquiriesFilter, statisticsFilter]) => {
        return this.api
          .listEnquiries({
            tenantId: (org as OrganisationDto).tenantId,
            organisationId: (org as OrganisationDto).id,
            startDate: statisticsFilter.startDate.toISOString(),
            endDate: statisticsFilter.endDate.toISOString(),
            sortBy: enquiriesFilter.sortBy,
            sortDirection: enquiriesFilter.sortDirection,
            limit: enquiriesFilter.limit,
            skip: enquiriesFilter.skip,
          })
          .pipe(
            map((res) => {
              return Enquiries.ResponseSuccessAllForSegmentAction({
                segment: action.segment,
                filter: { ...action.filter, ...enquiriesFilter, ...statisticsFilter },
                items: res,
                replace: action.clear,
              });
            }),
            catchError((error) =>
              of(
                Enquiries.ResponseFailAllForSegmentAction({
                  segment: action.segment,
                  error: error,
                }),
              ),
            ),
          );
      }),
    ),
  );

  fetchAfterFilterChange = createEffect((): any =>
    this.actions$.pipe(
      ofType(Enquiries.UpdateFilterForSegmentAction),
      map((action) => {
        return Enquiries.RequestAllForSegmentAction({
          segment: action.segment,
          filter: action.filter,
          clear: true,
        });
      }),
    ),
  );

  updateEnquiryAssignee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Enquiries.requestUpdateEnquiryAssignee),
      switchMap((action) => {
        return this.api
          .updateEnquiryAssignee({
            id: action.channelId,
            updateEnquiryAssigneeRequestDto: {
              assigneeId: action.assigneeId,
            },
          })
          .pipe(
            map((res) => {
              return Enquiries.successUpdateEnquiryAssignee({
                channelId: action.channelId,
                updatedEnquiry: res,
              });
            }),
            catchError((error) =>
              of(
                Enquiries.failUpdateEnquiryAssignee({
                  error: error,
                }),
              ),
            ),
          );
      }),
    ),
  );

  createEnquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Enquiries.requestCreateEnquiry),
      withLatestFrom(this.store.select(getSelectedId(ORGANISATIONS))),
      switchMap(([action, orgId]) => {
        console.log('Create enquiry effect: Org ID ', orgId);
        return this.api
          .createEnquiry({
            createEnquiryRequestDto: {
              organisationId: orgId as string,
            },
          })
          .pipe(
            map((res) => {
              return Enquiries.successCreateEnquiry({ enquiry: res });
            }),
            catchError((error) => of(Enquiries.failCreateEnquiry({ error }))),
          );
      }),
    ),
  );

  navigateOnEnquiryCreated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Enquiries.successCreateEnquiry),
        withLatestFrom(this.store.select(selectors.getSelected(ORGANISATIONS))),
        tap(([action, org]) => {
          this.router.navigate(
            [
              '/',
              (org as OrganisationDto).tenantId,
              'organisations',
              (org as OrganisationDto).id,
              'enquiries',
            ],
            {
              queryParams: { cid: action.enquiry.providerChatId },
              queryParamsHandling: 'replace',
            },
          );
        }),
      ),
    {
      dispatch: false,
    },
  );

  rateEnquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Enquiries.requestRateEnquiry),
      switchMap((action) => {
        return this.api
          .patchEnquiryRating({
            id: action.enquiryId,
            rateEnquiryDto: {
              rating: action.rating,
            },
          })
          .pipe(
            map((res) => {
              return Enquiries.successRateEnquiry({ updatedEnquiry: res });
            }),
            catchError((error) => of(Enquiries.failRateEnquiry({ error }))),
          );
      }),
    ),
  );

  setCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Enquiries.requestSetCategories),
      switchMap((action) => {
        return this.api
          .patchEnquiryCategories({
            id: action.enquiryId,
            setEnquiryCategoriesDto: {
              labels: action.categories,
            },
          })
          .pipe(
            map((res) => {
              return Enquiries.successSetCategories({ updatedEnquiry: res });
            }),
            catchError((error) => of(Enquiries.failSetCategories({ error }))),
          );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private api: EnquiriesService,
    private chatClientService: ChatClientService,
    private store: Store,
    private router: Router,
  ) {}
}
